import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormField,
  Header,
  Input,
  Link,
  Multiselect,
  RadioGroup,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { PageLayout, useAuth, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';
import { useNavigate } from 'react-router';
import { PersonAutosuggest, type PersonPickerEntry } from '../rbac/PersonAutosuggest';
import { useActiveAllianceOptions } from '../rbac/useActiveAlliances';
import DataSystemsPicker, { dataSectionError } from './catalog/DataSystemsPicker';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';
const GITHUB_ORG_PREFIX = 'https://github.com/Deloitte-US-Consulting/';
const TEMPLATE_URL = 'https://github.com/Deloitte-US-Consulting/gp-app-template';
const APP_ID_PATTERN = /^[a-z0-9-]+$/;

type Api = ReturnType<typeof createApiClient>;
interface Option { value: string; label: string }

/** Every platform role a builder can name as a visibility gate for their
 *  app — deliberately broader than the self-service-requestable catalog
 *  (`?requestable=true`, used by AccessRequests.tsx to request to JOIN a
 *  role): declaring "requires Lead Alliance Partner" doesn't require the
 *  submitter to hold or grant LAP themselves, so AirTable-sourced titles
 *  (LAP/AM) belong here even though nobody can self-request them. See
 *  api/src/rbac/platform-roles.ts's isPlatformScopeRole. */
function usePlatformRoleOptions(api: Api): Option[] {
  const [options, setOptions] = useState<Option[]>([]);
  useEffect(() => {
    api
      .get<{ id: string; role_name: string }[]>('/api/rbac/role?scope=platform')
      .then((roles) => setOptions(roles.map((r) => ({ value: r.id, label: r.role_name }))))
      .catch(() => setOptions([]));
  }, [api]);
  return options;
}

const ALL_ALLIANCES_OPTION: Option = { value: '__all__', label: 'All alliances' };

/**
 * The intake form for a prospective app builder — the input side of the
 * onboarding-request domain (api/src/onboarding/requests.ts). Submitting
 * needs no permission; the API validates and rejects a duplicate appId.
 * Declared scope (role/alliance) prefills the registry's real access gate
 * once an admin approves — see OnboardingAdmin.tsx.
 */
export default function OnboardSubmit() {
  const { accessToken } = useAuth();
  const { notify } = useNotify();
  const navigate = useNavigate();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [appId, setAppId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [pointOfContactEmail, setPointOfContactEmail] = useState('');
  const [pointOfContactName, setPointOfContactName] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [appType, setAppType] = useState<'hosted' | 'linked'>('hosted');
  const [externalUrl, setExternalUrl] = useState('');
  const [requiredRole, setRequiredRole] = useState<Option[]>([]);
  const [requiredAlliance, setRequiredAlliance] = useState<Option[]>([]);
  const [wantsAi, setWantsAi] = useState(false);
  const [dataSystems, setDataSystems] = useState<string[]>([]);
  const [dataNeed, setDataNeed] = useState('');
  const [targetGoLive, setTargetGoLive] = useState('');
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [roster, setRoster] = useState<PersonPickerEntry[]>([]);
  useEffect(() => {
    api.get<PersonPickerEntry[]>('/api/rbac/roster').then(setRoster).catch(() => setRoster([]));
  }, [api]);
  const roleOptions = usePlatformRoleOptions(api);
  const allianceOptions = useActiveAllianceOptions(api);
  const allianceMultiselectOptions = useMemo(
    () => [ALL_ALLIANCES_OPTION, ...allianceOptions],
    [allianceOptions],
  );

  const appIdError = (): string | undefined => {
    const v = appId.trim();
    if (!v) return 'App ID is required.';
    if (v.length > 64) return 'App ID must be 64 characters or fewer.';
    if (!APP_ID_PATTERN.test(v)) return 'App ID must use only lowercase letters, digits, and hyphens.';
    return undefined;
  };
  const displayNameError = (): string | undefined => (displayName.trim() ? undefined : 'Display name is required.');
  const repoUrlError = (): string | undefined => {
    const v = repoUrl.trim();
    if (!v) return 'Repo URL is required.';
    if (!v.startsWith(GITHUB_ORG_PREFIX) || v === GITHUB_ORG_PREFIX) {
      return `Repo URL must start with ${GITHUB_ORG_PREFIX}`;
    }
    return undefined;
  };
  const pocError = (): string | undefined =>
    pointOfContactEmail.trim() && /\S+@\S+\.\S+/.test(pointOfContactEmail) ? undefined : 'A valid point-of-contact email is required.';
  const descriptionError = (): string | undefined => (description.trim() ? undefined : 'Description is required.');
  const externalUrlError = (): string | undefined => {
    if (appType !== 'linked') return undefined;
    const v = externalUrl.trim();
    if (!v) return 'External URL is required for a linked app.';
    try {
      if (new URL(v).protocol !== 'https:') return 'External URL must use https://.';
    } catch {
      return 'External URL must be an absolute https:// URL.';
    }
    return undefined;
  };

  // The template attestation only makes sense for a Hosted app — a Linked
  // app already exists elsewhere and was never built from the template.
  const formValid =
    !appIdError() &&
    !displayNameError() &&
    !repoUrlError() &&
    !pocError() &&
    !descriptionError() &&
    !externalUrlError() &&
    !(appType === 'hosted' && dataSectionError(dataSystems, dataNeed)) &&
    (appType === 'linked' || attested);

  const submit = async () => {
    if (!formValid) return;
    setSubmitting(true);
    try {
      await api.post('/api/onboarding', {
        appId: appId.trim(),
        displayName: displayName.trim(),
        repoUrl: repoUrl.trim(),
        pointOfContactEmail: pointOfContactEmail.trim(),
        pointOfContactName: pointOfContactName || undefined,
        description: description.trim(),
        appType,
        externalUrl: appType === 'linked' ? externalUrl.trim() : undefined,
        requiredRole: requiredRole.length ? requiredRole.map((r) => r.value) : undefined,
        requiredAlliance:
          requiredAlliance.length && requiredAlliance[0]?.value !== ALL_ALLIANCES_OPTION.value
            ? requiredAlliance.map((a) => a.value)
            : undefined,
        wantsAi,
        dataSystems: appType === 'hosted' ? dataSystems : [],
        dataNeed: appType === 'hosted' && dataSystems.length ? dataNeed.trim() : undefined,
        targetGoLive: targetGoLive || undefined,
        attestedFromTemplate: appType === 'hosted' ? attested : false,
      });
      notify({ type: 'success', header: 'Submitted for review', content: `${displayName.trim()} was submitted to the platform team.` });
      navigate('/onboard/mine');
    } catch (e) {
      notify({ type: 'error', header: 'Submit failed', content: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout title="Submit an app" description="Tell us about your app so a platform admin can review and onboard it.">
      <Form
        actions={
          <Button variant="primary" loading={submitting} disabled={!formValid} onClick={submit}>
            Submit for review
          </Button>
        }
      >
        <SpaceBetween size="l">
          <FormField
            label="App ID"
            description={
              <>
                Lowercase letters, digits, and hyphens — the same name you picked when you created your repo from{' '}
                <Link external href={TEMPLATE_URL}>gp-app-template</Link>.
              </>
            }
            errorText={appIdError()}
          >
            <Input value={appId} onChange={({ detail }) => setAppId(detail.value)} placeholder="e.g. pursuits-tracker" />
          </FormField>
          <FormField label="Display name" errorText={displayNameError()}>
            <Input value={displayName} onChange={({ detail }) => setDisplayName(detail.value)} />
          </FormField>
          <FormField label="GitHub repo URL" description="Must be a repo under Deloitte-US-Consulting." errorText={repoUrlError()}>
            <Input
              value={repoUrl}
              onChange={({ detail }) => setRepoUrl(detail.value)}
              placeholder={`${GITHUB_ORG_PREFIX}your-app-repo`}
            />
          </FormField>
          <FormField label="Point of contact" description="Search the platform roster or the Microsoft directory." errorText={pocError()}>
            <PersonAutosuggest
              value={pointOfContactEmail}
              onChange={(value, name) => {
                setPointOfContactEmail(value);
                setPointOfContactName(name ?? null);
              }}
              people={roster}
              api={api}
              placeholder="Search by name or email…"
            />
          </FormField>
          <FormField label="Description" errorText={descriptionError()}>
            <Textarea value={description} onChange={({ detail }) => setDescription(detail.value)} rows={3} />
          </FormField>

          <Header variant="h3">App type</Header>
          <SpaceBetween size="xs">
            <Box color="text-body-secondary">
              <strong>Hosted</strong> means you built this app using the GP Platform starter (
              <Link external href={TEMPLATE_URL}>gp-app-template</Link>) — the steps at the top of the{' '}
              <Link href="/onboard" onFollow={(e) => { e.preventDefault(); navigate('/onboard'); }}>
                Get started
              </Link>{' '}
              page. The platform builds, runs, and displays it as a real part of the GP Platform.
            </Box>
            <Box color="text-body-secondary">
              <strong>Linked</strong> means the app already exists somewhere else — its own website, with its own
              login — and you just want a tile in the GP Platform that takes people there. You didn't build it
              from the template, and the platform won't host or run any of it.
            </Box>
          </SpaceBetween>
          <FormField label="Which one is this?">
            <RadioGroup
              value={appType}
              onChange={({ detail }) => setAppType(detail.value as 'hosted' | 'linked')}
              items={[
                {
                  value: 'hosted',
                  label: 'Hosted — built from gp-app-template, the platform runs it for me',
                },
                {
                  value: 'linked',
                  label: "Linked — it already exists elsewhere; just add a link to it",
                },
              ]}
            />
          </FormField>
          {appType === 'linked' && (
            <FormField
              label="Where does it live?"
              description="The web address people should be taken to when they click your app's tile."
              errorText={externalUrlError()}
            >
              <Input value={externalUrl} onChange={({ detail }) => setExternalUrl(detail.value)} placeholder="https://example.com/" />
            </FormField>
          )}

          <Header variant="h3">Declared scope</Header>
          <FormField label="Required role(s)" description="Who should be able to see this app? Leave blank if everyone should.">
            <Multiselect
              selectedOptions={requiredRole}
              placeholder="Choose one or more roles"
              options={roleOptions}
              onChange={({ detail }) => setRequiredRole(detail.selectedOptions as Option[])}
              filteringType="auto"
            />
          </FormField>
          <FormField
            label="Required alliance(s)"
            description="Which alliances is this app for? Choose All alliances (or leave blank) if it's not alliance-scoped."
          >
            <Multiselect
              selectedOptions={requiredAlliance}
              placeholder="Choose one or more alliances, or All alliances"
              options={allianceMultiselectOptions}
              onChange={({ detail }) => {
                const selected = detail.selectedOptions as Option[];
                const pickedAll = selected.some((o) => o.value === ALL_ALLIANCES_OPTION.value);
                const wasAll = requiredAlliance.some((o) => o.value === ALL_ALLIANCES_OPTION.value);
                setRequiredAlliance(
                  pickedAll && !wasAll
                    ? [ALL_ALLIANCES_OPTION]
                    : selected.filter((o) => o.value !== ALL_ALLIANCES_OPTION.value),
                );
              }}
              filteringType="auto"
            />
          </FormField>

          <Header variant="h3">Platform capabilities</Header>
          <Checkbox checked={wantsAi} onChange={({ detail }) => setWantsAi(detail.checked)}>
            This app needs the built-in Claude AI assistant
          </Checkbox>
          {appType === 'hosted' && (
            <DataSystemsPicker
              api={api}
              selected={dataSystems}
              need={dataNeed}
              onChange={(systems, need) => {
                setDataSystems(systems);
                setDataNeed(need);
              }}
            />
          )}
          <FormField label="Target go-live date (optional)">
            <DatePicker value={targetGoLive} onChange={({ detail }) => setTargetGoLive(detail.value)} placeholder="YYYY-MM-DD" />
          </FormField>

          {appType === 'hosted' && (
            <>
              <Checkbox checked={attested} onChange={({ detail }) => setAttested(detail.checked)}>
                This app was created from gp-app-template and I've tried it on my own computer.
              </Checkbox>
              {!attested && (
                <Box color="text-status-error">The template attestation is required for a hosted app.</Box>
              )}
            </>
          )}
        </SpaceBetween>
      </Form>
    </PageLayout>
  );
}
